import { generateTicks, type RulerUnit } from "@/lib/measure/ruler";

const LEN = { major: 34, medium: 22, minor: 13 } as const;

/** An SVG ruler edge: ticks + cm/inch labels along one side, actual-size. */
export function RulerStrip({
  length,
  pxPerMm,
  unit,
  orientation,
  thickness = 74,
}: {
  length: number;
  pxPerMm: number;
  unit: RulerUnit;
  orientation: "horizontal" | "vertical";
  thickness?: number;
}) {
  const ticks = generateTicks(length, pxPerMm, unit);
  const horizontal = orientation === "horizontal";
  const w = horizontal ? length : thickness;
  const h = horizontal ? thickness : length;

  return (
    <svg
      width={w}
      height={h}
      viewBox={`0 0 ${w} ${h}`}
      className="text-foreground"
      role="img"
      aria-label="Ruler"
      data-testid="ruler-strip"
    >
      <title>Ruler</title>
      {ticks.map((t) => {
        const len = LEN[t.size];
        const line = horizontal
          ? { x1: t.pos, y1: 0, x2: t.pos, y2: len }
          : { x1: 0, y1: t.pos, x2: len, y2: t.pos };
        return (
          <g key={`${t.pos}-${t.size}`}>
            <line
              {...line}
              stroke="currentColor"
              strokeWidth={t.size === "major" ? 1.4 : 1}
              opacity={t.size === "minor" ? 0.55 : 0.9}
            />
            {t.label !== undefined && (
              <text
                x={horizontal ? t.pos + 3 : len + 4}
                y={horizontal ? len + 13 : t.pos + 4}
                fontSize="11"
                fill="currentColor"
                opacity={0.8}
              >
                {t.label}
              </text>
            )}
          </g>
        );
      })}
      {/* Unit tag near the origin */}
      <text
        x={horizontal ? 4 : thickness - 22}
        y={horizontal ? thickness - 8 : 14}
        fontSize="11"
        fontWeight="700"
        fill="currentColor"
        opacity={0.7}
      >
        {unit}
      </text>
    </svg>
  );
}
